import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  List,
  ListItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useSelector } from "react-redux";
import { AxiosError } from "axios";

import apiService from "../../../service/Api";
import { getCalendarPalette, tokens, type CalendarPalette } from "../../../theme";
import { RootState } from "../../../store/Index";
import { PilotAPIResponse, PilotProps } from "../types/view-pilot";

interface AvailabilityProps {
  pilot?: PilotProps | null;
}

interface CallsheetProps {
  id: number;
  assignment: Array<{
    role: string;
    notes: string | null;
  }>;
  crew_member: PilotAPIResponse;
  stream: {
    id: number;
    title: string;
    date: string;
    start: string;
    end?: string;
    address_one: string | null;
    address_two: string | null;
    suburb: string | null;
    city: string | null;
  };
  status?: "accepted" | "declined" | "pending";
}

export interface CrewAvailabilitySlot {
  id: number;
  crew_member: number;
  crew_member_name?: string;
  day_of_week: string;
  day_of_week_display?: string;
  start_time: string;
  end_time: string;
  duration_hours?: number;
  is_recurring?: boolean;
  is_active?: boolean;
  effective_from?: string;
  effective_until?: string | null;
  notes?: string;
  is_currently_available?: boolean;
  created_at?: string;
  updated_at?: string;
}

const WEEKDAY_ORDER = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

/** Horizontal hour lines + solid fill (lines sit on top of fill). */
function hourGridBackground(fill: string, gridLine: string): object {
  return {
    backgroundImage: `repeating-linear-gradient(to bottom, transparent 0, transparent ${
      PX_PER_HOUR - 1
    }px, ${gridLine} ${PX_PER_HOUR - 1}px, ${gridLine} ${PX_PER_HOUR}px), linear-gradient(${fill}, ${fill})`,
    backgroundSize: "100% 100%",
  };
}

function getMondayStartWeekDates(ref = new Date()): Date[] {
  const d = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
  const jsDay = d.getDay();
  const offset = jsDay === 0 ? -6 : 1 - jsDay;
  d.setDate(d.getDate() + offset);
  return WEEKDAY_ORDER.map((_, i) => {
    const col = new Date(d);
    col.setDate(d.getDate() + i);
    return col;
  });
}

const formatClock = (t: string) => {
  if (!t) return "";
  return t.length >= 5 ? t.slice(0, 5) : t;
};

/** Week grid: first line 6am, last boundary 10pm (17 hour marks, 16h body). */
const CALENDAR_START_HOUR = 6;
const CALENDAR_END_HOUR = 22;
const CALENDAR_HOUR_COUNT = CALENDAR_END_HOUR - CALENDAR_START_HOUR;
const PX_PER_HOUR = 52;
const GRID_BODY_HEIGHT = CALENDAR_HOUR_COUNT * PX_PER_HOUR;
const TIME_GUTTER_WIDTH = 56;

function formatHourLabel(hour24: number): string {
  const h12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const ampm = hour24 < 12 ? "am" : "pm";
  return `${h12} ${ampm}`;
}

function parseTimeToMinutes(t: string): number | null {
  const clock = formatClock(t);
  const [hStr, mStr] = clock.split(":");
  const h = Number.parseInt(hStr ?? "", 10);
  const m = Number.parseInt(mStr ?? "", 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

/** e.g. 05:00–18:00 → `0500-1800`; overnight supported (e.g. `2200-0200`). */
function formatSlotRangeCompact24(start: string, end: string): string {
  const sm = parseTimeToMinutes(start);
  const em = parseTimeToMinutes(end);
  if (sm == null || em == null) return "";
  let endAbs = em;
  if (endAbs <= sm) {
    endAbs += 24 * 60;
  }
  const pad = (n: number) => n.toString().padStart(2, "0");
  const startH = Math.floor(sm / 60);
  const startMin = sm % 60;
  const endH = Math.floor(endAbs / 60) % 24;
  const endMin = endAbs % 60;
  return `${pad(startH)}${pad(startMin)}-${pad(endH)}${pad(endMin)}`;
}

function getSlotBlockStyle(slot: CrewAvailabilitySlot): {
  topPct: number;
  heightPct: number;
} | null {
  const startM = parseTimeToMinutes(slot.start_time);
  const endM = parseTimeToMinutes(slot.end_time);
  if (startM == null || endM == null) return null;

  const start = startM;
  let end = endM;
  if (end <= start) {
    end += 24 * 60;
  }

  const windowStart = CALENDAR_START_HOUR * 60;
  const windowEnd = CALENDAR_END_HOUR * 60;
  const rangeMin = windowEnd - windowStart;

  const clampedStart = Math.max(start, windowStart);
  const clampedEnd = Math.min(end, windowEnd);
  if (clampedEnd <= clampedStart) return null;

  const topPct = ((clampedStart - windowStart) / rangeMin) * 100;
  const heightPct = ((clampedEnd - clampedStart) / rangeMin) * 100;
  return { topPct, heightPct: Math.max(heightPct, 1.2) };
}

const formatEffectiveRange = (slot: CrewAvailabilitySlot) => {
  const from = slot.effective_from
    ? format(new Date(slot.effective_from), "dd MMM yyyy")
    : null;
  const until = slot.effective_until
    ? format(new Date(slot.effective_until), "dd MMM yyyy")
    : null;
  if (from && until) return `${from} – ${until}`;
  if (from) return `From ${from}`;
  return null;
};

function AvailabilitySlotTooltipContent({
  slot,
  palette,
}: {
  slot: CrewAvailabilitySlot;
  palette: CalendarPalette;
}) {
  const range = formatEffectiveRange(slot);
  return (
    <Box sx={{ p: 0.5, minWidth: 200 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75, color: palette.textPrimary }}>
        {formatSlotRangeCompact24(slot.start_time, slot.end_time) ||
          `${formatClock(slot.start_time)} – ${formatClock(slot.end_time)}`}
      </Typography>
      {slot.day_of_week_display ? (
        <Typography variant="caption" sx={{ color: palette.textSecondary, display: "block" }}>
          {slot.day_of_week_display}
        </Typography>
      ) : null}
      {slot.is_recurring ? (
        <Typography variant="caption" sx={{ color: palette.textSecondary, display: "block" }}>
          Recurring weekly
        </Typography>
      ) : null}
      {range ? (
        <Typography variant="caption" sx={{ color: palette.textSecondary, display: "block" }}>
          {range}
        </Typography>
      ) : null}
      {slot.notes ? (
        <Typography variant="caption" sx={{ color: palette.textSecondary, display: "block", mt: 0.5 }}>
          {slot.notes}
        </Typography>
      ) : null}
      {slot.is_active === false ? (
        <Typography variant="caption" sx={{ color: palette.inactiveBorder, display: "block", mt: 0.5 }}>
          Inactive
        </Typography>
      ) : null}
      {slot.is_currently_available ? (
        <Typography variant="caption" sx={{ color: palette.liveBorder, display: "block", mt: 0.5 }}>
          In this window now
        </Typography>
      ) : null}
    </Box>
  );
}

function normalizeAvailabilityPayload(data: unknown): CrewAvailabilitySlot[] {
  if (Array.isArray(data)) {
    return data as CrewAvailabilitySlot[];
  }
  if (data && typeof data === "object" && "results" in data) {
    return (data as { results?: CrewAvailabilitySlot[] }).results ?? [];
  }
  return [];
}

function groupSlotsByDay(slots: CrewAvailabilitySlot[]) {
  const byDay = new Map<string, CrewAvailabilitySlot[]>();
  for (const d of WEEKDAY_ORDER) {
    byDay.set(d, []);
  }
  for (const slot of slots) {
    const key = slot.day_of_week?.toLowerCase();
    if (key && byDay.has(key)) {
      byDay.get(key)!.push(slot);
    }
  }
  for (const d of WEEKDAY_ORDER) {
    byDay.get(d)!.sort((a, b) => a.start_time.localeCompare(b.start_time));
  }
  return byDay;
}

export default function Availability({ pilot }: AvailabilityProps) {
  const [upcomingAssignments, setUpcomingAssignments] = useState<
    CallsheetProps[]
  >([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [assignmentsError, setAssignmentsError] = useState<string | null>(null);

  const [weeklySlots, setWeeklySlots] = useState<CrewAvailabilitySlot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);

  const [currentStatus, setCurrentStatus] =
    useState<PilotProps["available_status"]>("available");

  const navigate = useNavigate();
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = useMemo(
    () => tokens(theme.palette.mode, reduxTheme),
    [theme.palette.mode, reduxTheme],
  );
  const GC = useMemo(
    () => getCalendarPalette(theme.palette.mode, colors),
    [theme.palette.mode, colors],
  );

  const weekColumnDates = useMemo(() => getMondayStartWeekDates(), []);
  const todayYmd = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);

  const fetchUpcomingAssignments = useCallback(async () => {
    if (!pilot?.id) return;

    setAssignmentsLoading(true);
    setAssignmentsError(null);

    try {
      const response = await apiService.get<{ results: CallsheetProps[] }>(
        "/api/call-sheets/callsheet/upcoming/",
        { crew_member: pilot.id },
      );

      setUpcomingAssignments(response.data.results || []);
    } catch (error) {
      if (error instanceof AxiosError) {
        setAssignmentsError(
          error.response?.data?.message ||
            "Failed to load upcoming assignments. Please try again.",
        );
      } else {
        setAssignmentsError("Failed to load upcoming assignments. Please try again.");
      }
    } finally {
      setAssignmentsLoading(false);
    }
  }, [pilot?.id]);

  const fetchWeeklyAvailability = useCallback(async () => {
    if (!pilot?.id) return;

    setSlotsLoading(true);
    setSlotsError(null);

    try {
      const response = await apiService.get<unknown>(
        `/api/crews/crew-availabilities/crew-member/${pilot.id}/`,
        { page_size: 500 },
      );
      setWeeklySlots(normalizeAvailabilityPayload(response.data));
    } catch (error) {
      if (error instanceof AxiosError) {
        setSlotsError(
          error.response?.data?.detail ||
            error.response?.data?.message ||
            "Failed to load weekly availability.",
        );
      } else {
        setSlotsError("Failed to load weekly availability.");
      }
      setWeeklySlots([]);
    } finally {
      setSlotsLoading(false);
    }
  }, [pilot?.id]);

  const fetchCurrentStatus = useCallback(async () => {
    if (!pilot?.id) return;

    try {
      const response = await apiService.get<{ results: CallsheetProps[] }>(
        "/api/call-sheets/callsheet/",
        { crew_member: pilot.id, page_size: 100 },
      );
      
      // Use status from callsheet instead of old logic
      const callsheets = response.data.results;
      const acceptedCallsheet = callsheets.find(cs => cs.status === "accepted");
      
      if (acceptedCallsheet) {
        setCurrentStatus("on_stream");
      } else if (callsheets.length > 0) {
        // Has callsheet but not accepted means assigned
        setCurrentStatus("assigned");
      } else {
        setCurrentStatus("available");
      }
    } catch (error) {
      console.error("Failed to resolve current status from callsheets:", error);
      setCurrentStatus("available");
    }
  }, [pilot?.id]);

  useEffect(() => {
    fetchUpcomingAssignments();
    fetchWeeklyAvailability();
    fetchCurrentStatus();
  }, [fetchCurrentStatus, fetchUpcomingAssignments, fetchWeeklyAvailability]);

  if (!pilot) return null;

  const slotsByDay = groupSlotsByDay(weeklySlots);

  return (
    <Box sx={{ display: "flex", minHeight: 520 }}>
      <Box
        sx={{
          width: 360,
          flexShrink: 0,
          backgroundColor: colors.primary[900],
          borderRight: "1px solid",
          borderColor: "divider",
          p: 2,
          overflowY: "auto",
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Upcoming Assignments
        </Typography>

        {assignmentsLoading ? (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={18} />
            <Typography variant="body2">Loading assignments...</Typography>
          </Box>
        ) : assignmentsError ? (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={fetchUpcomingAssignments}
              >
                Retry
              </Button>
            }
          >
            {assignmentsError}
          </Alert>
        ) : upcomingAssignments.length === 0 ? (
          <Chip
            size="small"
            sx={{ borderRadius: 1, textTransform: "none" }}
            label={<i>No upcoming assignments for {pilot.name} yet.</i>}
          />
        ) : (
          <List sx={{ mb: 3 }}>
            {upcomingAssignments.map((assignment) => (
              <ListItem key={assignment.id} sx={{ px: 0, py: 1 }}>
                <Card sx={{ width: "100%", boxShadow: 1 }}>
                  <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 1,
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, color: colors.primary[400] }}
                      >
                        {format(
                          new Date(assignment.stream.date),
                          "dd MMM, yyyy",
                        ).toUpperCase()}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          navigate(`/stream-management/view/${assignment.stream.id}`)
                        }
                        sx={{ minWidth: "auto", fontSize: "0.75rem" }}
                      >
                        View
                      </Button>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {assignment.stream.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {assignment.assignment[0]?.role || "N/A"}
                    </Typography>
                  </CardContent>
                </Card>
              </ListItem>
            ))}
          </List>
        )}

        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Insights
        </Typography>
        <Paper sx={{ p: 2, backgroundColor: colors.primary[800] }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Current status: {currentStatus}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Status reflects today&apos;s callsheet assignments (on stream vs assigned today vs
            free). Weekly hours come from availability slots.
          </Typography>
        </Paper>
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          bgcolor: GC.surface,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ px: 2.5, pt: 2.5, pb: 2, borderBottom: `1px solid ${GC.border}` }}>
          <Typography
            component="h2"
            sx={{
              fontWeight: 400,
              fontSize: "1.375rem",
              color: GC.textPrimary,
              mb: 0.5,
              letterSpacing: "-0.01em",
            }}
          >
            Weekly availability
          </Typography>
          <Typography variant="body2" sx={{ color: GC.textSecondary, mb: 2, maxWidth: 720 }}>
            Week view with hourly grid (6am–10pm). Tinted column is today; hover a block for
            details.
          </Typography>

          <Stack direction="row" spacing={2.5} sx={{ flexWrap: "wrap", rowGap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "2px",
                  bgcolor: GC.liveBg,
                  borderLeft: `3px solid ${GC.liveBorder}`,
                }}
              />
              <Typography variant="caption" sx={{ color: GC.textSecondary }}>
                In this time window now
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "2px",
                  bgcolor: GC.eventBg,
                  borderLeft: `3px solid ${GC.accent}`,
                }}
              />
              <Typography variant="caption" sx={{ color: GC.textSecondary }}>
                Scheduled availability
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "2px",
                  bgcolor: GC.inactiveBg,
                  borderLeft: `3px solid ${GC.inactiveBorder}`,
                }}
              />
              <Typography variant="caption" sx={{ color: GC.textSecondary }}>
                Inactive slot
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, overflow: "auto" }}>
          {slotsLoading ? (
            <Box
              display="flex"
              alignItems="center"
              gap={1.5}
              sx={{ py: 6, px: 2.5, justifyContent: "center" }}
            >
              <CircularProgress size={24} sx={{ color: GC.accent }} />
              <Typography variant="body2" sx={{ color: GC.textSecondary }}>
                Loading calendar…
              </Typography>
            </Box>
          ) : slotsError ? (
            <Box sx={{ p: 2.5 }}>
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={fetchWeeklyAvailability}>
                    Retry
                  </Button>
                }
              >
                {slotsError}
              </Alert>
            </Box>
          ) : (
            <Box
              sx={{
                px: { xs: 1, sm: 2 },
                pb: 2,
                minWidth: 0,
                overflowX: "auto",
              }}
            >
              <Box
                sx={{
                  minWidth: { xs: 560, sm: 640 },
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    borderBottom: `1px solid ${GC.border}`,
                  }}
                >
                  <Box
                    sx={{
                      width: TIME_GUTTER_WIDTH,
                      flexShrink: 0,
                      bgcolor: GC.columnHeader,
                    }}
                  />
                  {WEEKDAY_ORDER.map((day, index) => {
                    const colDate = weekColumnDates[index];
                    const colYmd = format(colDate, "yyyy-MM-dd");
                    const isToday = colYmd === todayYmd;
                    return (
                      <Box
                        key={day}
                        sx={{
                          flex: 1,
                          minWidth: 72,
                          py: 1.25,
                          px: 0.5,
                          textAlign: "center",
                          bgcolor: isToday ? GC.todayColumn : GC.columnHeader,
                          borderLeft: `1px solid ${GC.border}`,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.6875rem",
                            fontWeight: 500,
                            color: GC.textSecondary,
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                          }}
                        >
                          {format(colDate, "EEE")}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "1.25rem",
                            fontWeight: isToday ? 600 : 400,
                            color: GC.textPrimary,
                            lineHeight: 1.2,
                          }}
                        >
                          {format(colDate, "d")}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>

                <Box sx={{ display: "flex", flexDirection: "row", alignItems: "stretch" }}>
                  <Box
                    sx={{
                      width: TIME_GUTTER_WIDTH,
                      flexShrink: 0,
                      height: GRID_BODY_HEIGHT,
                      position: "relative",
                      borderRight: `1px solid ${GC.border}`,
                      borderTop: `1px solid ${GC.border}`,
                      ...hourGridBackground(GC.columnHeader, GC.gridLine),
                    }}
                  >
                    {Array.from({ length: CALENDAR_HOUR_COUNT }, (_, i) => {
                      const hour = CALENDAR_START_HOUR + i;
                      return (
                        <Typography
                          key={hour}
                          sx={{
                            position: "absolute",
                            left: 4,
                            top: i * PX_PER_HOUR + 2,
                            fontSize: "0.6875rem",
                            color: GC.textSecondary,
                            lineHeight: 1.2,
                            userSelect: "none",
                          }}
                        >
                          {formatHourLabel(hour)}
                        </Typography>
                      );
                    })}
                    <Typography
                      sx={{
                        position: "absolute",
                        left: 4,
                        bottom: 2,
                        fontSize: "0.6875rem",
                        color: GC.textSecondary,
                        lineHeight: 1.2,
                        userSelect: "none",
                      }}
                    >
                      {formatHourLabel(CALENDAR_END_HOUR)}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: "flex",
                      flexDirection: "row",
                      position: "relative",
                      height: GRID_BODY_HEIGHT,
                      borderTop: `1px solid ${GC.border}`,
                    }}
                  >
                    {WEEKDAY_ORDER.map((day, index) => {
                      const daySlots = slotsByDay.get(day) ?? [];
                      const colDate = weekColumnDates[index];
                      const colYmd = format(colDate, "yyyy-MM-dd");
                      const isToday = colYmd === todayYmd;
                      const hasBlocks = daySlots.some((s) => getSlotBlockStyle(s) != null);

                      const columnFill = isToday ? GC.todayColumnFill : GC.surface;

                      return (
                        <Box
                          key={day}
                          sx={{
                            flex: 1,
                            minWidth: 72,
                            position: "relative",
                            zIndex: 1,
                            borderLeft: index === 0 ? "none" : `1px solid ${GC.border}`,
                            ...hourGridBackground(columnFill, GC.gridLine),
                          }}
                        >
                          {!hasBlocks ? (
                            <Typography
                              variant="caption"
                              sx={{
                                position: "absolute",
                                left: "50%",
                                top: 6,
                                transform: "translateX(-50%)",
                                color: GC.textSecondary,
                                textAlign: "center",
                                px: 0.5,
                                pt: 0.25,
                                pointerEvents: "none",
                                maxWidth: "calc(100% - 8px)",
                                zIndex: 3,
                              }}
                            >
                              {daySlots.length === 0
                                ? "No availability"
                                : "Outside 6am–10pm"}
                            </Typography>
                          ) : null}

                          {daySlots.map((slot) => {
                            const layout = getSlotBlockStyle(slot);
                            if (!layout) return null;

                            const inactive = slot.is_active === false;
                            const live = slot.is_currently_available === true;
                            const accentColor = live
                              ? GC.liveBorder
                              : inactive
                                ? GC.inactiveBorder
                                : GC.accent;
                            const fillColor = live
                              ? GC.liveBg
                              : inactive
                                ? GC.inactiveBg
                                : GC.eventBg;

                            return (
                              <Tooltip
                                key={slot.id}
                                title={<AvailabilitySlotTooltipContent slot={slot} palette={GC} />}
                                placement="top"
                                arrow
                                enterDelay={200}
                                slotProps={{
                                  tooltip: {
                                    sx: {
                                      bgcolor: GC.surface,
                                      color: GC.textPrimary,
                                      border: `1px solid ${GC.border}`,
                                      borderRadius: 2,
                                      boxShadow: GC.tooltipShadow,
                                      maxWidth: 320,
                                      p: 0,
                                      "& .MuiTooltip-arrow": {
                                        color: GC.surface,
                                        "&::before": {
                                          border: `1px solid ${GC.border}`,
                                        },
                                      },
                                    },
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    position: "absolute",
                                    left: 2,
                                    right: 2,
                                    top: `${layout.topPct}%`,
                                    height: `${layout.heightPct}%`,
                                    zIndex: 2,
                                    borderLeft: `4px solid ${accentColor}`,
                                    bgcolor: fillColor,
                                    borderRadius: "4px",
                                    boxShadow: GC.slotShadow,
                                    cursor: "default",
                                    overflow: "hidden",
                                    minHeight: 4,
                                    opacity: 0.98,
                                    display: "flex",
                                    alignItems: "center",
                                    pl: "6px",
                                    pr: 0.25,
                                    "&:hover": {
                                      boxShadow: GC.slotShadowHover,
                                      opacity: 1,
                                    },
                                  }}
                                >
                                  <Typography
                                    component="span"
                                    sx={{
                                      fontSize: "0.65rem",
                                      fontWeight: 600,
                                      color: GC.textPrimary,
                                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                                      lineHeight: 1.15,
                                      letterSpacing: "0.02em",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                   {formatSlotRangeCompact24(slot.start_time, slot.end_time) ||
                                      `${formatClock(slot.start_time)}-${formatClock(slot.end_time)}`}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            );
                          })}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        
      </Box>
    </Box>
  );
}
